'use client'

import { Box, Typography, Stack } from '@mui/material'
import { FlightRouteInfo } from '@/src/types/flight'
import { useTranslations } from 'use-intl'
import { getFlightLogoUrl } from '@/src/utils/generalUtils/airlineImage'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { convertDDMMYYYYToISO } from '@/src/utils/date/convertDateFormat'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'

type Props = {
    selectedFlightIndex: number
    setSelectedFlightIndex: (index: number) => void
    fareFamilySectors?: FlightRouteInfo[]
}

export default function FareSelectionFlightSelector({
    selectedFlightIndex,
    setSelectedFlightIndex,
    fareFamilySectors,
}: Props) {
    const t = useTranslations('common')
    const tFareFamily = useTranslations('fareFamily')
    const { formatDate } = useFormatDate()
    const locale = getCurrentLocale()
    const isArabic = locale === 'ar';
    const sectorInfo = fareFamilySectors?.map((sector) => {
        const firstSegment = sector?.flights?.segments[0] ?? {};
        const airlineName = firstSegment.flightName
        const airlineLogo = getFlightLogoUrl(firstSegment.flightNumber.split('-')[0])
        const formattedDate = convertDDMMYYYYToISO(sector.firstDeparture.date);
        return {
            time: `${sector.firstDeparture.time} – ${sector.lastArrival.time}`,
            stops: sector.stops === 0 ? t('Non-stop') : tFareFamily('stops', { count: sector.stops }),
            origin: sector.airportCodes[0],
            destination: sector.airportCodes[sector.airportCodes.length - 1],
            date: formatDate(formattedDate,'ddd, DD MMM'),
            logo: airlineLogo,
            airlineName: airlineName,
        }
    })

    return (
        <Stack
            direction="row"
            spacing={4}
            mb={1}
            sx={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
            }}
        >
            {sectorInfo?.map((sector, index) => (
                <Box
                    key={index}
                    onClick={() => setSelectedFlightIndex(index)}
                    sx={{
                        cursor: 'pointer',
                        textAlign: 'center',
                        borderBottom: selectedFlightIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 2,
                        minWidth: 'max-content',
                        pb:'3px'
                    }}
                >
                    <Box sx={{ textAlign: 'center',display:'flex', flexDirection:'column',alignItems:'center',justifyContent:'center' }}>
                        <Box
                            sx={{
                                width: '2rem',
                                height: '2rem',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                border: '1px solid #ccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fff',
                            }}
                        >
                            <Box component="img" src={sector.logo} alt={sector.airlineName} sx={{ width: '1.5rem', height: '1.5rem', objectFit: 'contain' }} />
                        </Box>
                        <Typography variant="caption" fontWeight={500} display="block">
                            {sector.airlineName}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'start',display:'flex',alignItems:'flex-start',flexDirection:'column',justifyContent:'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                                {sector.time}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                {`(${sector.stops})`}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="caption" component="span">
                                {sector.origin}
                            </Typography>
                            <Typography variant="caption" component="span" sx={{ mx: 0.5 }}>
                                {isArabic ? '←' : '→'}
                            </Typography>
                            <Typography variant="caption" component="span">
                                {sector.destination}
                            </Typography>
                            <Typography variant="caption" component="span" sx={{ mx: 0.5 }}>
                                ·
                            </Typography>
                            <Typography variant="caption" component="span">
                                {sector.date}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ))}
        </Stack>
    )
}
